import { SEARCH_RATE_LIMIT_UPDATE_CONFIG_URL } from '@1po/1po-bff-fe-spec/generated/backoffice/BackofficeURLs';
import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import {
  SearchRateLimitConfig,
  SearchRateLimitState,
  SearchRateLimitViolation,
  SearchRateLimitViolationMetrics,
} from './SearchRateLimit.types';

/**
 * Redux slice for search rate limit configuration and state.
 * <p>
 * Manages:
 * - Current rate limit config (global + garage overrides)
 * - Loading state
 * - Error state
 * - Cache invalidation flag
 * </p>
 */

const emptyMetrics: SearchRateLimitViolationMetrics = {
  todayCount: 0,
  weekCount: 0,
  topViolator: null,
};

const initialState: SearchRateLimitState = {
  config: null,
  loading: false,
  error: null,
  lastUpdated: null,
  isDirty: false,
  violations: [],
  totalViolationCount: 0,
  shortTermMetrics: null,
  longTermMetrics: null,
  violationsLoading: false,
};

const searchRateLimitSlice = createSlice({
  name: 'searchRateLimit',
  initialState,
  reducers: {
    // Request actions
    fetchSearchRateLimitConfigRequest: (state, _action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },

    setSearchRateLimitConfig: (state, action: PayloadAction<SearchRateLimitConfig>) => {
      state.config = action.payload;
      state.loading = false;
      state.error = null;
      state.lastUpdated = new Date().toISOString();
      state.isDirty = false;
    },

    updateSearchRateLimitConfigRequest: (state, action: PayloadAction<SearchRateLimitConfig>) => {
      state.loading = true;
      state.error = null;
      state.isDirty = true;
      state.config = action.payload;
    },

    updateSearchRateLimitConfigSuccess: (state, action: PayloadAction<SearchRateLimitConfig>) => {
      state.config = action.payload;
      state.loading = false;
      state.error = null;
      state.lastUpdated = new Date().toISOString();
      state.isDirty = false;
    },

    updateSearchRateLimitConfigFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.isDirty = false;
    },

    fetchSearchRateLimitConfigFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Reset actions
    resetSearchRateLimitState: (state) => {
      state.config = null;
      state.loading = false;
      state.error = null;
      state.lastUpdated = null;
      state.isDirty = false;
    },

    clearSearchRateLimitError: (state) => {
      state.error = null;
    },

    invalidateSearchRateLimitCache: (state) => {
      state.lastUpdated = null;
    },

    fetchSearchRateLimitViolationsRequest: (state, _action: PayloadAction<string>) => {
      state.violationsLoading = true;
    },

    setSearchRateLimitViolations: (
      state,
      action: PayloadAction<{
        violations: SearchRateLimitViolation[];
        totalCount: number;
        shortTermMetrics?: SearchRateLimitViolationMetrics | null;
        longTermMetrics?: SearchRateLimitViolationMetrics | null;
      }>,
    ) => {
      state.violations = action.payload.violations;
      state.totalViolationCount = action.payload.totalCount;
      state.shortTermMetrics = action.payload.shortTermMetrics ?? emptyMetrics;
      state.longTermMetrics = action.payload.longTermMetrics ?? emptyMetrics;
      state.violationsLoading = false;
    },

    fetchSearchRateLimitViolationsFailure: (state) => {
      state.violationsLoading = false;
    },

    fetchSearchRateLimitConfigNotFound: (state) => {
      state.loading = false;
    },
  },
});

export const searchRateLimitUpdateConfigTimedOut = createAction(SEARCH_RATE_LIMIT_UPDATE_CONFIG_URL);

export const {
  fetchSearchRateLimitConfigRequest,
  setSearchRateLimitConfig,
  updateSearchRateLimitConfigRequest,
  updateSearchRateLimitConfigSuccess,
  updateSearchRateLimitConfigFailure,
  fetchSearchRateLimitConfigFailure,
  fetchSearchRateLimitConfigNotFound,
  resetSearchRateLimitState,
  clearSearchRateLimitError,
  invalidateSearchRateLimitCache,
  fetchSearchRateLimitViolationsRequest,
  setSearchRateLimitViolations,
  fetchSearchRateLimitViolationsFailure,
} = searchRateLimitSlice.actions;

export default searchRateLimitSlice.reducer;
